<template>
  <div class="card modal-card">
    <div class="modal-card-body pd-0">
      <header class="bandeau-modal card-header"></header>
      <div class="mx-6 my-5">
        <h1 class="title is-size-3">
          {{ $t(title) }}
        </h1>
      </div>
      <div class="mx-6 my-5 columns">
        <div class="column is-narrow pdl-0">
          <form @submit.prevent="recherche()">
            <b-input
              v-model="auto"
              :placeholder="$t('general.autocomplete.search')"
              type="search"
              icon="magnify"
              expanded>
            </b-input>
          </form>
        </div>
      </div>
      <div class="mx-6 my-5 overflow-auto height-500">
        <v-server-table
          v-if="mountedLoadingEnd"
          url=""
          :columns="columns"
          :options="options"
          :ref="referenceTable"
          @loaded="finLoading"
          @loading="debutLoading"
          class="vueTableFilterStyle">
          <template #afterTable>
            <table-items-in-view @input="$refs[referenceTable].setLimit($event)" />
          </template>
          <template #addSelection="{ row }">
            <button
              @click="selectClient(row)"
              :class="isInSelection(row) ? 'is-success' : 'is-primary'"
              class="ripple button width-100percent">
              <b-icon icon="plus-circle"></b-icon>
            </button>
          </template>
          <template #code="{ row }">
            <div class="is-flex is-justify-content-space-between is-align-items-start">
              <div class="level-left">
                <router-link-blank
                  :to="{ name: routeDetail, params: { id: row.id } }"
                  :class="{'text-access-no-permission':!checkPermissions(constantes.TIERS.lecture)}"
                  :message="row.code"
                  class="lien">
                </router-link-blank>
              </div>
              <div class="is-flex is-flex-direction-column mgl-2">
                <tag-list
                  v-if="getListeTags(row)"
                  :listes="getListeTags(row)"/>
              </div>
              <div class="level-right is-flex">
                <niveau-blocage-icone
                  v-if="row.blocageLevel"
                  :niveau="row.blocageLevel"
                  :description="row.descriptionBlocage"
                  size="is-small"/>
              </div>
            </div>
          </template>
          <template #competencesFournisseur="{ row }">
            <tag-list
                v-if="getCompetencesFournisseur(row).length > 0"
                :listes="getCompetencesFournisseur(row)"/>
          </template>
          <template #sousTypes="{ row }">
            <b-taglist>
              <b-tag
                v-for="sousType in getSousTypesLibelle(row.sousTypes)"
                :key="sousType"
                type="is-primary">
                {{ sousType }}
              </b-tag>
            </b-taglist>
          </template>
          <template #adresse="{ row }">
            <span v-if="row.adressePostale">{{row.adressePostale.adresse1}}</span>
          </template>
          <template #codePostal="{ row }">
            <span v-if="row.adressePostale">{{row.adressePostale.codePostal}}</span>
          </template>
          <template #ville="{ row }">
            <span v-if="row.adressePostale">{{row.adressePostale.ville}}</span>
          </template>
          <template #nomCommercial="{ row }">
            {{row.nomCommercial}}
            <b-tooltip
              v-if="row.prospect"
              :label="$t('listeClients.table.prospect')"
              class="mgl-10px"
              type="is-warning"
              position="is-right">
              <b-icon
                icon="account-search"
                class="is-cursor-pointer mgr-5px"
                size="is-small"
                type="is-warning">
              </b-icon>
            </b-tooltip>
          </template>
          <template
            v-if="isForTiersFournisseurType && hasMoreThanOneSousType"
            #[`filter__sousTypes`]>
            <libelle-tag-input
              v-model="sousTypesFournisseur"
              @input="debounceTagFournisseur"
              :items="sousTypesFournisseurListe"
              label="">
            </libelle-tag-input>
          </template>
          <template
            v-if="searchByCompetenceFournisseur"
            #[`filter__competencesFournisseur`]>
            <libelle-tag-input
              v-model="competenceFournisseur"
              @input="debounceRecherche"
              :items="competenceFournisseurs"
              field="libelle"
              label="">
            </libelle-tag-input>
          </template>
        </v-server-table>
      </div>
      <footer class="mx-6 my-5">
        <button
          class="button is-cancel btn-modal-annuler"
          @click="$parent.close()">
          {{ $t('general.button.close') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { createNamespacedHelpers } from 'vuex';
import { getAll } from '@/api/tiers/index';
import { getAllWithTiers as getAllContactByTiers } from '@/api/contacts';
import TagList from '@/components/general/tagList';
import RouterLinkBlank from '@/components/general/routerLinkBlank';
import TableItemsInView from '@/components/general/TableItemsInView';
import LibelleTagInput from '@/components/general/tagInput/libelleTagInput';
import messageErreur from '@/services/general/messageErreur';
import { columnsSplice } from '@/services/general/columns';
import {
  TEXTS, RESULTS_PER_PAGE,
  SORT_ICON, NO_PER_PAGE_VALUE,
} from '@/configuration/clientTableConf';
import {
  TIERS_TYPE_CLIENT,
  TIERS_SOUS_TYPE_AUCUN,
  TIERS_SOUS_TYPE_AUTRE,
  TIERS_SOUS_TYPE_SERVICE,
  TIERS_TYPE_FOURNISSEUR,
  LISTE_SOUS_TYPE_FOURNISSEURS_PAGES,
} from '@/configuration/tiers/tiersConf';
import { TIERS } from '@/configuration/permissionsBodacConf';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import { REFERENCE_TABLE_CLIENT } from '@/configuration/general/referenceTableConf';
import niveauBlocageIcone from '@/components/general/NiveauBlocageIcone';

const permissions = createNamespacedHelpers('permissions');
const societe = createNamespacedHelpers('societe');
const Parametres = createNamespacedHelpers('parametres');

export default {
  name: 'p-advanced-search',
  props: {
    selections: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      required: false,
      default: 'ajoutDevis.table.advancedSearchContact.title',
    },
    keyId: {
      type: String,
      required: false,
      default: 'tiersId',
    },
    addContacts: {
      type: Boolean,
      required: false,
      default: true,
    },
    type: {
      type: String,
      required: false,
      default: TIERS_TYPE_CLIENT,
    },
    sousTypes: {
      type: Array,
      required: false,
      default: () => [TIERS_SOUS_TYPE_AUCUN],
    },
    searchByCompetenceFournisseur: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      clients: [],
      totalClients: 0,
      loadingComponent: null,
      constantes: {
        TIERS,
      },
      referenceTable: REFERENCE_TABLE_CLIENT,
      auto: '',
      sousTypesFournisseur: [],
      sousTypesFournisseurListe: [],
      competenceFournisseur: [],
      mountedLoadingEnd: false,
    };
  },
  components: {
    TableItemsInView,
    RouterLinkBlank,
    LibelleTagInput,
    TagList,
    niveauBlocageIcone,
  },
  computed: {
    ...permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    hasMoreThanOneSousType() {
      return this.sousTypes.length > 1;
    },
    isForTiersFournisseurType() {
      return this.type === TIERS_TYPE_FOURNISSEUR;
    },
    routeDetail() {
      if (this.type === TIERS_TYPE_CLIENT) {
        return 'c.bodac.clients/contacts-client';
      }
      if (this.isForTiersFournisseurType) {
        return 'c.achat.fournisseurs';
      }
      return null;
    },
    competenceFournisseurs() {
      return this.parametre(PARAMETRES_KEYS.competenceFournisseur)
        .filter(c => c.actif);
    },
    columnsData() {
      return [
        { name: 'addSelection', filterable: false, sortable: false },
        { name: 'code', filterable: true, sortable: true },
        { name: 'nomCommercial', filterable: true, sortable: true },
        { name: 'competencesFournisseur', filterable: false, sortable: false },
        { name: 'nomJuridique', filterable: true, sortable: true },
        { name: 'interne', filterable: this.isForTiersFournisseurType, sortable: true },
        { name: 'sousTypes', filterable: false, sortable: true },
        { name: 'adresse', filterable: true, sortable: true },
        { name: 'codePostal', filterable: true, sortable: true },
        { name: 'ville', filterable: true, sortable: true },
        { name: 'compteComptable', filterable: true, sortable: true },
      ];
    },
    columns() {
      const columns = this.columnsData.map(el => el.name);
      if (!this.searchByCompetenceFournisseur) {
        columnsSplice(columns, 'competencesFournisseur');
      }
      if (!this.isForTiersFournisseurType) {
        columnsSplice(columns, 'interne');
        columnsSplice(columns, 'sousTypes');
      }
      return columns;
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          nomCommercial: this.$i18n.t('listeClients.table.name'),
          nomJuridique: this.$i18n.t('listeClients.table.legalName'),
          interne: this.$i18n.t('listeClients.table.internal'),
          sousTypes: this.$i18n.t('listeClients.table.sousTypes'),
          adresse: this.$i18n.t('listeClients.table.address'),
          codePostal: this.$i18n.t('listeClients.table.zipCode'),
          ville: this.$i18n.t('listeClients.table.city'),
          compteComptable: this.$i18n.t('listeClients.table.accountingCode'),
          competencesFournisseur: this.$i18n.t('listeClients.table.competencesFournisseur'),
          addSelection: '',
        },
        debounce: 1000,
        sortable: this.columnsData.filter(el => el.sortable).map(el => el.name),
        filterable: this.columnsData.filter(el => el.filterable).map(el => el.name),
        filterByColumn: true,
        listColumns: {
          interne: [
            { id: true, text: this.$t('general.oui') },
            { id: false, text: this.$t('general.non') },
          ],
        },
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: TEXTS,
        columnsClasses: {
          addSelection: 'colonneSelected-35 is-paddingless',
          compteComptable: 'width-200px',
        },
        requestFunction: ((data) => {
          data.query.archive = false;
          data.query.type = this.type;
          data.query.sousType = this.sousTypes;
          data.query.societeId = this.societeActiveId;
          data.query.auto = this.auto;
          data.query.competenceFournisseurIds = this.competenceFournisseur.map(c => c.id);
          if (data.query.nomJuridique?.trim()) {
            data.query.nomsCommercialOuNomsJuridique = [data.query.nomJuridique?.trim()];
          }
          if (data.query.code?.trim()) data.query.codes = [data.query.code?.trim()];
          if (data.query.ville?.trim()) data.query.villes = [data.query.ville?.trim()];
          if (data.query.codePostal?.trim()) {
            data.query.codesPostaux = [data.query.codePostal?.trim()];
          }
          if (this.isForTiersFournisseurType) {
            data.query.sousType = this.sousTypesFournisseur.map(s => s.code);
          }
          if (!this.isForTiersFournisseurType) {
            delete data.query.interne;
          }
          delete data.query.code;
          delete data.query.nomJuridique;
          delete data.query.codePostal;
          delete data.query.ville;
          return this.loadClients(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.clients,
            count: this.totalClients,
          };
        }),
      };
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
    }),
    async loadClients(data) {
      const { items, totalCount } = (await getAll(data)).data.result;
      items.forEach((client) => {
        client.selected = false;
      });
      this.clients = items;
      this.totalClients = totalCount;
      return {};
    },
    getListeTags(row) {
      return row.tags?.map(t => t.tag) || null;
    },
    getCompetencesFournisseur(row) {
      return row.competencesFournisseur?.map(c => c.competenceFournisseur) || null;
    },
    getSousTypesLibelle(sousTypes) {
      return LISTE_SOUS_TYPE_FOURNISSEURS_PAGES
        .filter((type) => sousTypes.includes(type.code) && type.libelle)
        .map((type) => this.$t(type.libelle));
    },
    debounceTagFournisseur: debounce(function recherche(value) {
      if (!value.length) this.sousTypesFournisseur = this.sousTypesFournisseurListe;
      this.recherche();
    }, 1000),
    recherche() {
      this.$refs[this.referenceTable].refresh();
    },
    debounceRecherche: debounce(function recherche() {
      this.recherche();
    }, 1000),
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    finLoading() {
      this.loadingComponent.close();
    },
    isInSelection(row) {
      return this.selections.some(i => i[this.keyId] === row.id);
    },
    async selectClient(value) {
      const val = this.selections.find(i => i[this.keyId] === value.id);
      if (val) {
        this.$emit('removeClient', val);
        return;
      }
      if (!this.addContacts) {
        this.$emit('addClient', value);
        return;
      }
      this.debutLoading();
      try {
        value.contacts = (await getAllContactByTiers({
          query: {
            archive: false,
            tiersId: [value.id],
            type: TIERS_TYPE_CLIENT,
            societeId: this.societeActiveId,
          },
        })).data.result.items;
        this.$emit('addClient', value);
      } finally {
        this.finLoading();
      }
    },
  },
  async mounted() {
    this.debutLoading();
    try {
      await this.loadParametres([PARAMETRES_KEYS.competenceFournisseur]);
      if (this.isForTiersFournisseurType) {
        this.sousTypesFournisseurListe = LISTE_SOUS_TYPE_FOURNISSEURS_PAGES
          .filter(s => this.sousTypes.includes(s.code));
        this.sousTypesFournisseur = this.sousTypesFournisseurListe
          .filter(s => [TIERS_SOUS_TYPE_AUTRE, TIERS_SOUS_TYPE_SERVICE].includes(s.code));
        if (this.sousTypesFournisseur.length === 0) {
          this.sousTypesFournisseur = this.sousTypesFournisseurListe;
        }
      }
    } catch (e) {
      messageErreur(this.$i18n);
      throw e;
    } finally {
      this.finLoading();
      this.mountedLoadingEnd = true;
    }
  },
};
</script>
